import type {
  CompetitionView,
  EventBasedMarketView,
  EventId,
  EventTypeView,
  EventView,
  MarketId
} from '@ps/isp-web-services-types';
import { notEmpty } from '@ps/web-core-utils';
import { useSelector } from 'react-redux';

import { useWidget } from '@swf/core/hooks/useWidget';
import type { RootState } from '@swf/core/store';

import type { EventMarketTemplatesModel } from '../models/market-templates/eventBased';

interface NormalisedEventBasedData {
  event: EventView;
  eventType: EventTypeView;
  competition: CompetitionView;
  market: EventBasedMarketView | undefined;
  markets: Record<MarketId, EventBasedMarketView>;
}

type Props =
  | {
      marketId: MarketId;
    }
  | {
      eventId: EventId;
    };

export const useNormalisedEventBasedData = (props: Props): NormalisedEventBasedData => {
  const { widgetSelect } = useWidget<never, EventMarketTemplatesModel>();

  const market = useSelector((state: RootState) =>
    'marketId' in props ? widgetSelect.market(state, props.marketId) : undefined
  );

  const event = useSelector((state: RootState) =>
    widgetSelect.event(
      state,
      'eventId' in props ? props.eventId : (market?.eventId ?? Number.MIN_SAFE_INTEGER)
    )
  );

  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, event.competitionId)
  );

  const eventType = useSelector((state: RootState) =>
    widgetSelect.eventType(state, event.eventTypeId)
  );

  const partialMarkets = useSelector(widgetSelect.markets);
  const markets = Object.entries(partialMarkets).reduce<Record<MarketId, EventBasedMarketView>>(
    (acc, [key, val]) => {
      if (val?.runners.some(notEmpty)) {
        acc[key] = val;
      }
      return acc;
    },
    {}
  );

  return {
    competition,
    event,
    eventType,
    market,
    markets
  };
};
